import type { RouteObject } from "react-router";

/**
 * The Labour workspace's routes (M25 W0-C-03, contracts §15), in a file of their own like `staffRoutes.tsx` and
 * `studentsRoutes.tsx` — `index.tsx` mounts this under the shell in one line.
 *
 * One layout route for the whole workspace: `LabourWorkspaceGate` answers the add-on question once (the
 * entitlements, or any labour read refused with `402 ADDON_REQUIRED`) and shows `AddonGate` in the page's
 * place. Every page is a file in its owning lane's folder (`src/modules/labour/<area>/pages/`); a lane
 * replaces the file and never edits this table. Static segments come before the parameter beside them
 * (`labourers/register` before `labourers/:personId`).
 */
export const labourRoute: RouteObject = {
  path: "labour",
  lazy: async () => ({
    Component: (await import("@/modules/labour/shared/LabourWorkspaceGate")).LabourWorkspaceGate,
  }),
  children: [
    {
      index: true,
      handle: { title: "Today" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/today/pages/LabourTodayPage")).LabourTodayPage,
      }),
    },
    {
      // The directory; register, import and the photo round are static segments written BEFORE
      // `:personId`, so the router can never read the word "register" as a labourer.
      path: "labourers",
      handle: { title: "Labourers" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/labourers/pages/LabourersPage")).LabourersPage,
      }),
    },
    {
      path: "labourers/register",
      handle: { title: "Register labourer" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/labourers/pages/LabourRegisterPage")).LabourRegisterPage,
      }),
    },
    {
      path: "labourers/import",
      handle: { title: "Import labourers" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/labourers/pages/LabourImportPage")).LabourImportPage,
      }),
    },
    {
      path: "labourers/photo-round",
      handle: { title: "Photo round" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/labourers/pages/LabourPhotoRoundPage"))
          .LabourPhotoRoundPage,
      }),
    },
    {
      // Keyed on the shared `people.id`: a labourer IS a person (ADR-0017), so the core person page's
      // "Also in: Labour" link (`personClaims`) lands here.
      path: "labourers/:personId",
      handle: { title: "Labourer" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/labourers/pages/LabourerPage")).LabourerPage,
      }),
    },
    {
      path: "contractors",
      handle: { title: "Contractors" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/contractors/pages/LabourContractorsPage"))
          .LabourContractorsPage,
      }),
    },
    {
      path: "contractors/:contractorId",
      handle: { title: "Contractor" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/contractors/pages/LabourContractorPage"))
          .LabourContractorPage,
      }),
    },
    {
      path: "sites",
      handle: { title: "Sites" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/sites/pages/LabourSitesPage")).LabourSitesPage,
      }),
    },
    {
      path: "sites/:siteId",
      handle: { title: "Site" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/sites/pages/LabourSitePage")).LabourSitePage,
      }),
    },
    {
      // The day drawer is `?person&day` on this page, not a route of its own; the exceptions list
      // beneath it lights this entry too.
      path: "attendance",
      handle: { title: "Attendance" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/attendance/pages/LabourAttendancePage"))
          .LabourAttendancePage,
      }),
    },
    {
      path: "attendance/exceptions",
      handle: { title: "Exceptions" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/attendance/pages/LabourExceptionsPage"))
          .LabourExceptionsPage,
      }),
    },
    {
      // The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum).
      path: "reports",
      handle: { title: "Labour reports" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/reports/pages/LabourReportsPage")).LabourReportsPage,
      }),
    },
    {
      // `:kind` is the report's address segment (`daily-contractor` … `time-sheet`, contracts §13.2);
      // its page chooses the report.
      path: "reports/:kind",
      handle: { title: "Labour report" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/reports/pages/LabourReportPage")).LabourReportPage,
      }),
    },
    {
      path: "settings",
      handle: { title: "Labour settings" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/settings/pages/LabourSettingsPage")).LabourSettingsPage,
      }),
    },
    {
      // The setup guide (lane G): `?step=<id>`, `?person=<id>`. The breadcrumb's title is "Setup guide".
      path: "setup",
      handle: { title: "Setup guide" },
      lazy: async () => ({
        Component: (await import("@/modules/labour/setup/pages/LabourSetupPage")).LabourSetupPage,
      }),
    },
  ],
};
